import { ApplicationRef, Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { concat, fromEvent, interval, merge } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { RoutingService } from './service/routing.service';
import { ConfigService } from './service/config.service';
import { TranslateService } from '@ngx-translate/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { ConnectionService } from 'ng-connection-service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UserScopedStorageService } from './shared/services/user-scoped-storage.service';

const UPDATE_CHECK_INTERVAL = 30 * 60 * 1000;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  theme: string;
  isOnline: boolean;
  baseUrl: string;

  constructor(
    appRef: ApplicationRef,
    private route: ActivatedRoute,
    public routing: RoutingService,
    private translate: TranslateService,
    private updates: SwUpdate,
    private connectionService: ConnectionService,
    public dialog: MatDialog,
    public config: ConfigService,
    private snackBar: MatSnackBar,
    private userStorage: UserScopedStorageService,
  ) {
    // One-time cleanup of the pre-namespacing bare user keys (they were shared
    // across accounts on the device). User data now lives under per-user keys.
    this.userStorage.purgeLegacyGlobalKeys();

    this.updates.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_READY') {
        this.onVersionReady();
      }
    });

    if (this.updates.isEnabled) {
      this.updates.unrecoverable.subscribe(() => this.reloadPage());

      // Poll only once the app is stable, else the pending interval task
      // delays SW registration (registerWhenStable) by its full 30s timeout.
      concat(
        appRef.isStable.pipe(first((stable) => stable)),
        merge(
          interval(UPDATE_CHECK_INTERVAL),
          fromEvent(document, 'visibilitychange').pipe(
            filter(() => !document.hidden),
          ),
        ),
      ).subscribe(() =>
        this.updates
          .checkForUpdate()
          .catch((err) => console.debug('SW update check failed', err)),
      );
    }

    translate.setDefaultLang('en');

    routing.getTitle();

    this.route.queryParams.subscribe((params) => {
      const theme =
        params.theme ||
        (this.config.appConfig.setup.useStorage &&
          localStorage.getItem('theme')) ||
        this.config.appConfig.routing.theme;

      this.routing.inTheme(theme);
      this.theme = this.routing.theme;
      if (params.i18n) {
        this.translate.use(params.i18n);
      }
    });
  }

  private onVersionReady(): void {
    if (document.hidden) {
      this.reloadPage();
      return;
    }
    this.snackBar
      .open('A new version of Cockpit is available', 'Refresh')
      .onAction()
      .subscribe(() => this.reloadPage());
  }

  private reloadPage(): void {
    document.location.reload();
  }
}
